import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Square, Headphones, AudioLines, Waves, Sparkles, Leaf } from "lucide-react";

const OM = { id: "om", kind: "om", name: "Om · AUM (136.1 Hz)", freq: 136.1,
  benefit: "The primordial sound of the universe. Steadies the breath, calms the nervous system and dissolves the sense of separateness — deep peace and unity." };

const SOLFEGGIO = [
  { id: "s174", freq: 174, name: "174 Hz", benefit: "A natural anaesthetic — relieves pain and tension, grounds you into safety." },
  { id: "s285", freq: 285, name: "285 Hz", benefit: "Tissue healing & rejuvenation; signals the body to restore and repair." },
  { id: "s396", freq: 396, name: "396 Hz · Root", benefit: "Releases guilt & fear; grounds and secures you (Muladhara)." },
  { id: "s417", freq: 417, name: "417 Hz · Sacral", benefit: "Clears negativity and facilitates change and new beginnings." },
  { id: "s528", freq: 528, name: "528 Hz · Miracle", benefit: "The 'love' frequency — clarity, transformation and DNA repair." },
  { id: "s639", freq: 639, name: "639 Hz · Heart", benefit: "Harmonises relationships, connection, compassion and understanding." },
  { id: "s741", freq: 741, name: "741 Hz · Throat", benefit: "Detoxifies, awakens intuition and empowers self-expression." },
  { id: "s852", freq: 852, name: "852 Hz · Third Eye", benefit: "Awakens intuition and returns you to spiritual order." },
  { id: "s963", freq: 963, name: "963 Hz · Crown", benefit: "Connection to the divine, oneness and pure awareness." },
];

const BINAURAL = [
  { id: "delta", carrier: 120, beat: 2, name: "Delta · 2 Hz", benefit: "Deep, dreamless sleep and profound physical healing." },
  { id: "theta", carrier: 150, beat: 6, name: "Theta · 6 Hz", benefit: "Deep meditation, creativity, intuition and memory." },
  { id: "alpha", carrier: 180, beat: 10, name: "Alpha · 10 Hz", benefit: "Relaxed focus, calm and stress relief — the flow state." },
  { id: "beta", carrier: 200, beat: 18, name: "Beta · 18 Hz", benefit: "Alertness, concentration and active problem-solving." },
  { id: "gamma", carrier: 220, beat: 40, name: "Gamma · 40 Hz", benefit: "Peak awareness, insight and heightened perception." },
];

const NATURE = [
  { id: "ocean", name: "Ocean Waves", benefit: "Rolling sea waves — slows the breath and washes away stress and overthinking." },
  { id: "rain", name: "Gentle Rain", benefit: "Steady rainfall — a soothing white-noise blanket for focus and sleep." },
  { id: "forest", name: "Forest Birds", benefit: "Birdsong in a calm forest — lifts mood and restores gentle, open attention." },
  { id: "wind", name: "Soft Wind", benefit: "A slow mountain breeze — grounding, spacious and calming." },
  { id: "stream", name: "Flowing Stream", benefit: "A bubbling river — releases tension and invites easy, flowing thoughts." },
  { id: "night", name: "Night Crickets", benefit: "A warm night meadow — comforting and sleep-inducing." },
];

const DURATIONS = [3, 5, 10, 15];

export default function SoundHealing() {
  const [minutes, setMinutes] = useState(5);
  const [active, setActive] = useState(null); // track id
  const [remaining, setRemaining] = useState(0);
  const ctxRef = useRef(null);
  const nodesRef = useRef([]);
  const masterRef = useRef(null);
  const endTimer = useRef(null);
  const tick = useRef(null);
  const natTimers = useRef([]);

  const cleanup = () => {
    if (endTimer.current) clearTimeout(endTimer.current);
    if (tick.current) clearInterval(tick.current);
    natTimers.current.forEach((t) => clearInterval(t));
    natTimers.current = [];
    nodesRef.current.forEach((n) => { try { n.stop && n.stop(); } catch (_) {} try { n.disconnect(); } catch (_) {} });
    nodesRef.current = [];
    if (ctxRef.current) { try { ctxRef.current.close(); } catch (_) {} ctxRef.current = null; }
    masterRef.current = null;
  };

  useEffect(() => () => cleanup(), []);

  const stop = () => {
    const ctx = ctxRef.current, master = masterRef.current;
    if (ctx && master) {
      try {
        master.gain.cancelScheduledValues(ctx.currentTime);
        master.gain.setValueAtTime(master.gain.value, ctx.currentTime);
        master.gain.linearRampToValueAtTime(0.0001, ctx.currentTime + 1.2);
      } catch (_) {}
      setTimeout(cleanup, 1300);
    } else cleanup();
    if (tick.current) clearInterval(tick.current);
    if (endTimer.current) clearTimeout(endTimer.current);
    setActive(null);
    setRemaining(0);
  };

  const start = (track) => {
    if (active === track.id) { stop(); return; }
    cleanup();
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    ctxRef.current = ctx;
    const master = ctx.createGain();
    master.gain.value = 0.0001;
    master.connect(ctx.destination);
    masterRef.current = master;
    const target = 0.18;

    const nodes = [];
    const osc = (freq, gainVal, type = "sine") => {
      const o = ctx.createOscillator(); o.type = type; o.frequency.value = freq;
      const g = ctx.createGain(); g.gain.value = gainVal;
      o.connect(g); return { o, g };
    };

    if (track.kind === "binaural") {
      const merger = ctx.createChannelMerger(2);
      const l = osc(track.carrier, 1); const r = osc(track.carrier + track.beat, 1);
      l.g.connect(merger, 0, 0); r.g.connect(merger, 0, 1);
      merger.connect(master);
      l.o.start(); r.o.start();
      nodes.push(l.o, r.o, merger);
    } else if (track.kind === "nature") {
      // white-noise buffer, looped
      const buf = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
      const data = buf.getChannelData(0);
      for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
      const src = ctx.createBufferSource(); src.buffer = buf; src.loop = true;
      const filter = ctx.createBiquadFilter();
      const g = ctx.createGain(); g.gain.value = 0.9;
      src.connect(filter); filter.connect(g); g.connect(master);
      src.start(); nodes.push(src);

      if (track.id === "ocean") {
        filter.type = "lowpass"; filter.frequency.value = 500;
        const lfo = ctx.createOscillator(); lfo.frequency.value = 0.11;
        const lg = ctx.createGain(); lg.gain.value = 0.45; const base = ctx.createConstantSource(); base.offset.value = 0.5;
        lfo.connect(lg); lg.connect(g.gain); base.connect(g.gain);
        const flfo = ctx.createGain(); flfo.gain.value = 300; lfo.connect(flfo); flfo.connect(filter.frequency);
        lfo.start(); base.start(); nodes.push(lfo, base);
      } else if (track.id === "rain") {
        filter.type = "bandpass"; filter.frequency.value = 3200; filter.Q.value = 0.6; g.gain.value = 0.5;
      } else if (track.id === "wind") {
        filter.type = "lowpass"; filter.frequency.value = 500; g.gain.value = 0.6;
        const lfo = ctx.createOscillator(); lfo.frequency.value = 0.08;
        const flfo = ctx.createGain(); flfo.gain.value = 260; lfo.connect(flfo); flfo.connect(filter.frequency);
        lfo.start(); nodes.push(lfo);
      } else if (track.id === "stream") {
        // Broadband "rushing water" (no pitched LFO — that warbled). Highpass→lowpass noise
        // + gentle random amplitude gurgles for a natural bubbling brook.
        filter.type = "highpass"; filter.frequency.value = 700; filter.Q.value = 0.7;
        filter.disconnect();
        const lp = ctx.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = 3600; lp.Q.value = 0.4;
        filter.connect(lp); lp.connect(g); g.gain.value = 0.42;
        nodes.push(lp);
        const gurgle = () => {
          if (!ctxRef.current) return;
          const t = ctx.currentTime;
          g.gain.setTargetAtTime(0.3 + Math.random() * 0.24, t, 0.22);
          lp.frequency.setTargetAtTime(3200 + Math.random() * 1200, t, 0.4);
        };
        natTimers.current.push(setInterval(gurgle, 450));
      } else if (track.id === "forest") {
        filter.type = "lowpass"; filter.frequency.value = 350; g.gain.value = 0.25; // soft wind bed
        // random bird chirps
        const chirp = () => {
          if (!ctxRef.current) return;
          const o = ctx.createOscillator(); o.type = "sine";
          const cg = ctx.createGain(); cg.gain.value = 0.0001;
          o.connect(cg); cg.connect(master);
          const t = ctx.currentTime;
          const f0 = 2200 + Math.random() * 2200;
          o.frequency.setValueAtTime(f0, t);
          o.frequency.exponentialRampToValueAtTime(f0 * (1.3 + Math.random() * 0.5), t + 0.08);
          cg.gain.exponentialRampToValueAtTime(0.09, t + 0.03);
          cg.gain.exponentialRampToValueAtTime(0.0001, t + 0.22);
          o.start(t); o.stop(t + 0.3);
        };
        natTimers.current.push(setInterval(() => { const n = 1 + Math.floor(Math.random() * 3); for (let i = 0; i < n; i++) setTimeout(chirp, Math.random() * 900); }, 1400));
      } else if (track.id === "night") {
        filter.type = "lowpass"; filter.frequency.value = 200; g.gain.value = 0.12;
        const cricket = () => {
          if (!ctxRef.current) return;
          const o = ctx.createOscillator(); o.type = "square"; o.frequency.value = 4600;
          const cg = ctx.createGain(); cg.gain.value = 0.0001;
          o.connect(cg); cg.connect(master);
          const t = ctx.currentTime;
          for (let k = 0; k < 5; k++) { cg.gain.setValueAtTime(0.05, t + k * 0.04); cg.gain.setValueAtTime(0.0001, t + k * 0.04 + 0.02); }
          o.start(t); o.stop(t + 0.28);
        };
        natTimers.current.push(setInterval(() => { if (Math.random() > 0.3) cricket(); }, 500));
      }
    } else if (track.kind === "om") {
      const filter = ctx.createBiquadFilter(); filter.type = "lowpass"; filter.frequency.value = 900;
      const parts = [osc(track.freq, 0.6), osc(track.freq * 2, 0.22), osc(track.freq / 2, 0.35), osc(track.freq * 3, 0.08)];
      parts.forEach((p) => { p.g.connect(filter); p.o.start(); nodes.push(p.o); });
      // gentle "chant" swell
      const lfo = ctx.createOscillator(); lfo.frequency.value = 0.12;
      const lfoGain = ctx.createGain(); lfoGain.gain.value = 0.05;
      const swell = ctx.createGain(); swell.gain.value = 0.9;
      lfo.connect(lfoGain); lfoGain.connect(swell.gain);
      filter.connect(swell); swell.connect(master);
      lfo.start(); nodes.push(lfo);
    } else {
      // solfeggio pure tone with a soft harmonic
      const filter = ctx.createBiquadFilter(); filter.type = "lowpass"; filter.frequency.value = 2200;
      const a = osc(track.freq, 0.7); const b = osc(track.freq * 2, 0.12);
      a.g.connect(filter); b.g.connect(filter); filter.connect(master);
      a.o.start(); b.o.start(); nodes.push(a.o, b.o);
    }
    nodesRef.current = nodes;

    // fade in
    try {
      master.gain.setValueAtTime(0.0001, ctx.currentTime);
      master.gain.exponentialRampToValueAtTime(target, ctx.currentTime + 2.5);
    } catch (_) { master.gain.value = target; }

    setActive(track.id);
    const total = minutes * 60;
    setRemaining(total);
    tick.current = setInterval(() => setRemaining((s) => (s > 0 ? s - 1 : 0)), 1000);
    endTimer.current = setTimeout(stop, total * 1000);
  };

  const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  const Card = ({ track, icon: Icon }) => {
    const on = active === track.id;
    return (
      <button data-testid={`sound-${track.id}`} onClick={() => start(track)}
        className={`text-left rounded-2xl border p-4 transition-colors ${on ? "border-brand bg-brand/10" : "border-rud bg-white/5 hover:border-brand/50"}`}>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 font-medium"><Icon size={16} className="text-brand" /> {track.name}</div>
          {on ? <Square size={16} className="text-brand" /> : <Play size={16} className="text-sec" />}
        </div>
        <p className="text-xs text-sec mt-2 leading-relaxed">{track.benefit}</p>
        {on && <div className="mt-2 text-xs text-brand font-medium" data-testid="sound-remaining">Playing · {fmt(remaining)} left</div>}
      </button>
    );
  };

  return (
    <div data-testid="sound-healing-panel">
      {/* duration selector */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="sound-durations">
        <span className="text-xs text-sec mr-1">Duration</span>
        {DURATIONS.map((m) => (
          <button key={m} data-testid={`sound-duration-${m}`} onClick={() => setMinutes(m)}
            className={`h-8 px-3 rounded-full text-sm transition-colors ${minutes === m ? "bg-brand text-black font-medium" : "bg-white/5 border border-white/10 text-sec"}`}>
            {m} min
          </button>
        ))}
        {active && <button data-testid="sound-stop" onClick={stop} className="btn-ghost rounded-full h-8 px-4 text-sm flex items-center gap-1.5 ml-auto"><Square size={14} /> Stop</button>}
      </div>

      <div className="mt-6">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-brand mb-3"><Sparkles size={13} /> Sacred Sound</div>
        <Card track={OM} icon={AudioLines} />
      </div>

      <div className="mt-6">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-brand mb-3"><Waves size={13} /> Solfeggio Frequencies</div>
        <div className="grid sm:grid-cols-2 gap-3">
          {SOLFEGGIO.map((t) => <Card key={t.id} track={{ ...t, kind: "tone" }} icon={Waves} />)}
        </div>
      </div>

      <div className="mt-6">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-brand mb-2"><Headphones size={13} /> Binaural Beats</div>
        <p className="text-[11px] text-sec mb-3 flex items-center gap-1.5"><Headphones size={12} /> Use headphones for binaural beats to work.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {BINAURAL.map((t) => <Card key={t.id} track={{ ...t, kind: "binaural" }} icon={Headphones} />)}
        </div>
      </div>

      <div className="mt-6">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-brand mb-3"><Leaf size={13} /> Nature Sounds</div>
        <div className="grid sm:grid-cols-2 gap-3">
          {NATURE.map((t) => <Card key={t.id} track={{ ...t, kind: "nature" }} icon={Leaf} />)}
        </div>
      </div>

      <p className="text-[11px] text-sec mt-6 leading-relaxed">
        Sounds are generated live on your device. Find a comfortable seat, relax your breath, and let the resonance settle your mind.
        Not a medical treatment — for relaxation and meditation only.
      </p>
    </div>
  );
}
